import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Recipe } from "@/components/RecipeCard";

interface GeneratingRecipe {
    id: string;
    title: string;
    status: 'generating';
    created_at: string;
}

interface RecipeContextType {
    hasNewRecipe: boolean;
    newRecipeId: string | null;
    recipesGeneratingImages: Set<string>;
    isGeneratingRecipe: boolean;
    generatingRecipes: Map<string, GeneratingRecipe>;
    markRecipeSaved: (recipeId: string) => void;
    markImageGenerating: (recipeId: string) => void;
    markImageGenerated: (recipeId: string) => void;
    setGeneratingRecipe: (isGenerating: boolean) => void;
    addGeneratingRecipe: (recipeId: string, title?: string) => void;
    removeGeneratingRecipe: (recipeId: string) => void;
    updateGeneratingRecipe: (recipeId: string, updates: Partial<GeneratingRecipe>) => void;
    clearNewRecipe: () => void;
    newlyCompletedRecipes: Set<string>;
    clearNewlyCompletedRecipes: () => void;
    unseenRecipes: Set<string>;
    markRecipeAsSeen: (recipeId: string) => void;
    addRecipeToUnseen: (recipeId: string) => void;
    markAllRecipesAsSeen: () => void;
    getUnseenCount: () => number;
    cleanupStuckRecipes: () => Promise<void>;
    checkAndResetGenerationState: () => Promise<void>;
}

const RecipeContext = createContext<RecipeContextType | undefined>(undefined);

export const RecipeProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useAuth();
    const [hasNewRecipe, setHasNewRecipe] = useState(false);
    const [newRecipeId, setNewRecipeId] = useState<string | null>(null);
    const [recipesGeneratingImages, setRecipesGeneratingImages] = useState<Set<string>>(new Set());
    const [isGeneratingRecipe, setIsGeneratingRecipe] = useState(false);
    const [generatingRecipes, setGeneratingRecipes] = useState<Map<string, GeneratingRecipe>>(new Map());
    const [newlyCompletedRecipes, setNewlyCompletedRecipes] = useState<Set<string>>(new Set());
    const [unseenRecipes, setUnseenRecipes] = useState<Set<string>>(new Set());
    const [generationTimeout, setGenerationTimeout] = useState<NodeJS.Timeout | null>(null);
    const [generationStartTime, setGenerationStartTime] = useState<number | null>(null);
    const [watchlistInitialized, setWatchlistInitialized] = useState(false);

    // Simple generation state management - no complex subscriptions

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (generationTimeout) {
                clearTimeout(generationTimeout);
            }
        };
    }, [generationTimeout]);

    // Check generation state on mount only (real-time updates handle the rest)
    useEffect(() => {
        if (!user) return;

        // Only check on mount if we're not currently generating
        // This prevents resetting the state during active generation
        if (!isGeneratingRecipe) {
            const initialTimeout = setTimeout(() => {
                checkAndResetGenerationState();
            }, 2000); // Short delay for initial check

            return () => clearTimeout(initialTimeout);
        }
    }, [user]); // Removed isGeneratingRecipe dependency to prevent race conditions

    const markRecipeSaved = (recipeId: string) => {
        setHasNewRecipe(true);
        setNewRecipeId(recipeId);
    };

    const markImageGenerating = (recipeId: string) => {
        setRecipesGeneratingImages(prev => new Set(prev).add(recipeId));
    };

    const markImageGenerated = (recipeId: string) => {
        setRecipesGeneratingImages(prev => {
            const newSet = new Set(prev);
            newSet.delete(recipeId);
            return newSet;
        });
    };

    const clearNewRecipe = () => {
        setHasNewRecipe(false);
        setNewRecipeId(null);
    };

    const setGeneratingRecipe = (isGenerating: boolean) => {
        // Clear existing timeout first
        if (generationTimeout) {
            clearTimeout(generationTimeout);
            setGenerationTimeout(null);
        }

        if (isGenerating) {
            setIsGeneratingRecipe(true);
            setGenerationStartTime(Date.now());

            // Set timeout for stuck generation (5 minutes)
            const timeout = setTimeout(() => {
                console.warn('Recipe generation timeout - stopping animation');
                setIsGeneratingRecipe(false);
                setGenerationStartTime(null);
                setGenerationTimeout(null);
                toast.error("זמן יצירת המתכון הסתיים", {
                    description: "יצירת המתכון לוקחת יותר זמן מהצפוי. המתכון יופיע בהיסטוריה כשהוא יהיה מוכן.",
                });
            }, 5 * 60 * 1000); // 5 minutes

            setGenerationTimeout(timeout);
        } else {
            // Only allow stopping if we've been generating for at least 10 seconds
            const now = Date.now();
            const minDuration = 10000; // 10 seconds minimum for recipe generation

            if (generationStartTime && (now - generationStartTime) < minDuration) {
                // Set a timeout to stop after the minimum duration
                const remainingTime = minDuration - (now - generationStartTime);
                const timeout = setTimeout(() => {
                    setIsGeneratingRecipe(false);
                    setGenerationStartTime(null);
                    setGenerationTimeout(null);
                }, remainingTime);

                setGenerationTimeout(timeout);
                return;
            }

            setIsGeneratingRecipe(false);
            setGenerationStartTime(null);
        }
    };

    const addGeneratingRecipe = useCallback((recipeId: string, title: string = 'מכינים מתכון חדש...') => {
        setGeneratingRecipes(prev => {
            // Deduplicate - skip if already exists
            if (prev.has(recipeId)) {
                return prev;
            }

            const generatingRecipe: GeneratingRecipe = {
                id: recipeId,
                title,
                status: 'generating',
                created_at: new Date().toISOString()
            };

            const newMap = new Map(prev);
            newMap.set(recipeId, generatingRecipe);
            return newMap;
        });
    }, []);

    const removeGeneratingRecipe = (recipeId: string) => {
        setGeneratingRecipes(prev => {
            const newMap = new Map(prev);
            newMap.delete(recipeId);
            return newMap;
        });
    };

    const updateGeneratingRecipe = (recipeId: string, updates: Partial<GeneratingRecipe>) => {
        setGeneratingRecipes(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(recipeId);
            if (existing) {
                newMap.set(recipeId, { ...existing, ...updates });
            }
            return newMap;
        });
    };

    // Initialize watchlist from database once when user logs in
    useEffect(() => {
        if (!user || watchlistInitialized) return;

        const initializeWatchlist = async () => {
            try {
                const { data, error } = await supabase
                    .from('saved_recipes')
                    .select('id, title, created_at')
                    .eq('user_id', user.id)
                    .eq('is_temporary', true)
                    .eq('status', 'generating');

                if (error) throw error;

                if (data && data.length > 0) {

                    // Add each recipe to watchlist (deduplication handled by addGeneratingRecipe)
                    data.forEach(recipe => {
                        addGeneratingRecipe(recipe.id, recipe.title || 'מכינים מתכון חדש...');
                    });
                }

                setWatchlistInitialized(true);
            } catch (error) {
                console.error('❌ Error initializing watchlist:', error);
                // Mark as initialized anyway to prevent infinite retry
                setWatchlistInitialized(true);
            }
        };

        // Delay initialization slightly to ensure everything is mounted
        const timeout = setTimeout(initializeWatchlist, 500);
        return () => clearTimeout(timeout);
    }, [user, watchlistInitialized, addGeneratingRecipe]);

    const clearNewlyCompletedRecipes = () => {
        setNewlyCompletedRecipes(new Set());
    };

    const markRecipeAsSeen = (recipeId: string) => {
        setUnseenRecipes(prev => {
            const newSet = new Set(prev);
            newSet.delete(recipeId);
            return newSet;
        });
    };

    const addRecipeToUnseen = (recipeId: string) => {
        setUnseenRecipes(prev => {
            const newSet = new Set(prev);
            newSet.add(recipeId);
            return newSet;
        });
    };

    const markAllRecipesAsSeen = () => {
        setUnseenRecipes(new Set());
    };

    const getUnseenCount = () => {
        return unseenRecipes.size;
    };

    const cleanupStuckRecipes = async () => {
        if (!user) return;

        try {
            // Find recipes that have been generating for more than 10 minutes
            const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();

            const { data: stuckRecipes, error } = await supabase
                .from('saved_recipes')
                .select('id, title, created_at')
                .eq('user_id', user.id)
                .eq('status', 'generating')
                .lt('created_at', tenMinutesAgo);

            if (error) {
                console.error('Error finding stuck recipes:', error);
                return;
            }

            if (stuckRecipes && stuckRecipes.length > 0) {
                console.log(`Found ${stuckRecipes.length} stuck recipes, cleaning up...`);

                // Delete stuck recipes
                const { error: deleteError } = await supabase
                    .from('saved_recipes')
                    .delete()
                    .eq('user_id', user.id)
                    .eq('status', 'generating')
                    .lt('created_at', tenMinutesAgo);

                if (deleteError) {
                    console.error('Error deleting stuck recipes:', deleteError);
                    toast.error("שגיאה בניקוי מתכונים תקועים", {
                        description: "לא הצלחנו לנקות מתכונים תקועים. נסה שוב מאוחר יותר.",
                    });
                } else {
                    console.log(`Successfully cleaned up ${stuckRecipes.length} stuck recipes`);
                    toast.success("נוקו מתכונים תקועים", {
                        description: `נמחקו ${stuckRecipes.length} מתכונים שלא הושלמו.`,
                    });
                }
            }
        } catch (error) {
            console.error('Error in cleanupStuckRecipes:', error);
            toast.error("שגיאה בניקוי מתכונים תקועים", {
                description: "אירעה שגיאה לא צפויה. נסה שוב מאוחר יותר.",
            });
        }
    };

    const checkAndResetGenerationState = async () => {
        if (!user) return;

        try {
            // Check if there are any currently generating recipes
            const { data: generatingRecipes, error } = await supabase
                .from('saved_recipes')
                .select('id, status')
                .eq('user_id', user.id)
                .eq('status', 'generating');

            if (error) {
                console.error('Error checking generating recipes:', error);
                return;
            }

            // If no recipes are generating but the state says we are, reset it
            // But only if we've been generating for at least 10 seconds
            if ((!generatingRecipes || generatingRecipes.length === 0) && isGeneratingRecipe) {
                const now = Date.now();
                const minDuration = 10000; // 10 seconds minimum

                if (generationStartTime && (now - generationStartTime) >= minDuration) {
                    setGeneratingRecipe(false);
                }
            }
        } catch (error) {
            console.error('Error in checkAndResetGenerationState:', error);
        }
    };


    return (
        <RecipeContext.Provider
            value={{
                hasNewRecipe,
                newRecipeId,
                recipesGeneratingImages,
                isGeneratingRecipe,
                generatingRecipes,
                markRecipeSaved,
                markImageGenerating,
                markImageGenerated,
                setGeneratingRecipe,
                addGeneratingRecipe,
                removeGeneratingRecipe,
                updateGeneratingRecipe,
                clearNewRecipe,
                newlyCompletedRecipes,
                clearNewlyCompletedRecipes,
                unseenRecipes,
                markRecipeAsSeen,
                addRecipeToUnseen,
                markAllRecipesAsSeen,
                getUnseenCount,
                cleanupStuckRecipes,
                checkAndResetGenerationState,
            }}
        >
            {children}
        </RecipeContext.Provider>
    );
};

export const useRecipe = () => {
    const context = useContext(RecipeContext);
    if (!context) {
        throw new Error("useRecipe must be used within RecipeProvider");
    }
    return context;
};

